<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEventsStore } from '@/stores/events'
import BetPanel from './Event/BetPanel.vue'
import EventOdds from './Event/Odds.vue'
import { formatSportTitle, setPageTitle } from '@/services/utils.js'
import Loading from '@/components/Loading.vue'

// Composables
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const eventsStore = useEventsStore()

// Reactive data  
const loading = computed(() => eventsStore.isLoading)
const event = computed(() => eventsStore.currentEvent)
const selectedBetType = ref('h2h') // 'h2h' or 'over_under'
const selectedOutcome = ref(null)

// Computed

const eventId = computed(() => route.params.id)

const hasExistingBet = computed(() => {
  return event.value?.user_bets && event.value.user_bets.length > 0
})

const isBettingDisabled = computed(() => {
  if (!event.value) return true
  //if (hasExistingBet.value) return true
  if (event.value.score && event.value.score.completed) return true
  const now = new Date()
  const eventTime = new Date(event.value.commence_time)
  const diffHours = (eventTime - now) / (1000 * 60 * 60)
  return diffHours < 0
})


const getEventStatus = (commenceTime) => {
  // Check if event has score data and is completed
  if (event.value?.score && event.value.score.completed) {
    return { text: t('events.status.finished'), color: 'success', key: 'finished' }
  }
  
  const now = new Date()
  const eventTime = new Date(commenceTime)
  const diffHours = (eventTime - now) / (1000 * 60 * 60)

  // If event time has passed but not completed, it's in progress
  if (diffHours < 0) {
    return { text: t('events.status.inProgress'), color: 'warning', key: 'inProgress' }
  } else if (diffHours < 2) {
    return { text: t('events.status.startingSoon'), color: 'error', key: 'startingSoon' }
  } else if (diffHours < 24) {
    return { text: t('events.status.today'), color: 'info', key: 'today' }
  } else {
    return { text: t('events.status.scheduled'), color: 'primary', key: 'scheduled' }
  }
}


const betMessage = ref('')
const betMessageType = ref('') // 'success' or 'error'
const showBetMessage = ref(false)

const showMessage = (payload) => {
  betMessage.value = payload.message
  betMessageType.value = payload.type
  showBetMessage.value = true
  setTimeout(() => {
    showBetMessage.value = false
  }, 5000)
}

const goBack = () => {
  router.go(-1)
}

const fetchEventDetails = async () => {
  try {
    await eventsStore.fetchEvent(eventId.value)
  } catch (error) {
    console.error('Error fetching event details:', error)
  }
}

const eventName = computed(() => {
  if (!event.value) return ''
  return `${event.value.home_team} - ${event.value.away_team}`
})

onMounted(async () => {
  await fetchEventDetails()
  setPageTitle(eventName.value)
})
</script>

<template>
  <v-container class="event-view">
    <!-- Bet Message Snackbar -->
    <v-snackbar
      v-model="showBetMessage"
      :color="betMessageType"
      :timeout="5000"
      location="top"
    >
      {{ betMessage }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="showBetMessage = false"
        >
          {{ $t('common.close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Loading State -->
    <Loading v-if="loading" />

    <!-- Event Details -->
    <div v-else-if="event" class="event-details">
      <!-- Header with Back Button -->
      <div class="d-flex align-center mb-6">
        <v-btn
          icon="mdi-arrow-left"
          variant="text"
          @click="goBack"
          class="mr-3"
        ></v-btn>
        <div>
          <div class="text-h6">{{ formatSportTitle(event.sport_key) }}</div>
          <!-- <p class="text-subtitle-1 text-medium-emphasis">{{ formatSportTitle(event.sport_key) }}</p> -->
        </div>
      </div>

      <v-row>
        <!-- Event Info Card -->
        <v-col cols="12" lg="8">
          <v-card class="mb-6" elevation="3">
            <v-card-title class="pb-2">
              <div class="w-100">
                <div class="event-top d-flex flex-column flex-sm-row justify-center justify-sm-space-between align-center mb-4 flex-wrap">
                  <v-chip
                    :color="getEventStatus(event.commence_time).color"
                    :size="$vuetify.display.xs ? 'small' : 'default'"
                  >
                    {{ getEventStatus(event.commence_time).text }}
                  </v-chip>
                  <!-- <v-chip
                    color="primary"
                    :size="$vuetify.display.xs ? 'small' : 'default'"
                    variant="outlined"
                  >
                    {{ formatSportTitle(event.sport_key) }}
                  </v-chip> -->
                </div>
                
                <!-- Match Info -->
                <div class="match-info-large">
                  <div class="teams-container-large mt-6">
                    <div class="team-large home-team">
                      <v-icon class="team-icon d-none d-sm-flex" :size="$vuetify.display.xs ? 24 : 32">mdi-home</v-icon>
                      <span class="team-name-large">{{ event.home_team }}</span>
                      <span v-if="event.score && event.score.completed" class="team-score-large">{{ event.score.score_1 }}</span>
                    </div>
                    
                    <div class="vs-section-large">
                      <span v-if="event.score?.completed" class="score-separator-large">-</span>

                      <div v-else-if="getEventStatus(event.commence_time).key === 'inProgress'" class="live-link-container">
                        <v-btn
                          :href="`https://www.google.com/search?q=${event.home_team.replace(/\s+/g, '+')}+${event.away_team.replace(/\s+/g, '+')}+live`"
                          target="_blank"
                          @click.stop
                          color="success"
                          variant="elevated"
                          :size="$vuetify.display.xs ? 'x-small' : 'small'"
                          class="live-btn"
                        >
                          <v-icon class="mr-1" :size="$vuetify.display.xs ? 12 : 16">mdi-play-circle</v-icon>
                          Live
                        </v-btn>
                      </div>
                      
                    </div>
                    
                    <div class="team-large away-team">
                      <span v-if="event.score && event.score.completed" class="team-score-large">{{ event.score.score_2 }}</span>
                      <span class="team-name-large">{{ event.away_team }}</span>
                      <v-icon class="team-icon d-none d-sm-flex" :size="$vuetify.display.xs ? 24 : 32">mdi-airplane</v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-title>

            <v-card-subtitle class="pb-sm-3 pt-0 pt-sm-3 text-center text-sm-start">
              <v-icon size="20" class="mr-2">mdi-clock-outline</v-icon>
              {{ $d(event.commence_time, 'long') }}
            </v-card-subtitle>

            <!-- Odds Display -->
            <v-card-text>
              <EventOdds
                :event="event"
                v-model:selected-bet-type="selectedBetType"
                v-model:selected-outcome="selectedOutcome"
                :is-betting-disabled="isBettingDisabled"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Betting Panel -->
        <v-col cols="12" lg="4">
          <BetPanel
            :event="event"
            :selectedOutcome="selectedOutcome"
            :isBettingDisabled="isBettingDisabled"
            @show-message="showMessage"
          />
        </v-col>
      </v-row>
    </div>

    <!-- Error State -->
    <div v-else class="text-center py-8">
      <v-icon size="64" color="error">mdi-alert-circle</v-icon>
      <h2 class="text-h5 mt-4">{{ $t('events.notFound.title') }}</h2>
      <p class="text-subtitle-1 text-medium-emphasis mb-4">
        {{ $t('events.notFound.subtitle') }}
      </p>
      <v-btn color="primary" @click="goBack">
        {{ $t('events.notFound.goBack') }}
      </v-btn>
    </div>
  </v-container>
</template>

<style scoped>
.event-top{
  gap: 8px;
}
.event-view {
  max-width: 1200px;
  margin: 0 auto;
}

.match-info-large {
  width: 100%;
}

.teams-container-large {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

@media (max-width: 599px) {
  .teams-container-large {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
}

.team-large {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

@media (max-width: 599px) {
  .team-large {
    flex-direction: column;
    gap: 4px;
    min-width: 100%;
  }
}

.home-team {
  justify-content: flex-start;
}

.away-team {
  justify-content: flex-end;
}

@media (max-width: 599px) {
  .home-team,
  .away-team {
    justify-content: center;
  }
}

.team-name-large {
  font-weight: 700;
  font-size: 1.5rem;
  color: #ddd;
}

.team-icon {
  color: #666;
}

@media (max-width: 599px) {
  .team-name-large {
    font-size: 1.2rem;
  }
  .teams-container-large {
    margin-bottom: 14px;
  }
}

@media (max-width: 400px) {
  .team-name-large {
    font-size: 1rem;
  }
}

.team-score-large {
  font-weight: 700;
  font-size: 1.8rem;
  color: #9cb69d;
  background: #2c2f2c;
  padding: 8px 12px;
  border-radius: 8px;
  min-width: 40px;
  text-align: center;
  display: inline-block;
}

@media (max-width: 599px) {
  .team-score-large {
    font-size: 1.4rem;
    padding: 6px 10px;
    min-width: 32px;
  }
}

.vs-section-large {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #999;
  min-width: 32px;
  justify-content: center;
}

@media (max-width: 599px) {
  .vs-section-large {
    min-width: 20px;
    margin: 8px 0;
    display: none;
  }
}

.live-link-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.live-btn {
  animation: pulse-live 2s infinite;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

@keyframes pulse-live {
  0% {
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  }
  50% {
    box-shadow: 0 4px 16px rgba(76, 175, 80, 0.5);
  }
  100% {
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  }
}

.score-separator-large {
  font-weight: 700;
  font-size: 1.5rem;
  color: #666;
}

@media (max-width: 599px) {
  .score-separator-large {
    font-size: 1.2rem;
  }
}



.betting-panel {
  position: sticky;
  top: 24px;
}

.selected-bet {
  background: rgba(25, 118, 210, 0.05);
  border-radius: 8px;
  padding: 12px;
}

.potential-winning {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
}

.v-table th,
.v-table td {
  padding: 8px 16px;
}
</style>